import { AyudaDePantalla } from './ayuda';
import { FlujoDeVenta } from '../services/table.service';
import { FeatureModule } from '../core/tenant/feature.model';

/** Lo que se guarda por usuario y negocio: si omitió el recorrido y qué pantallas ya vio en él. */
export interface EstadoDeBienvenida {
  omitido: boolean;
  vistos: string[];
  /** A7: las pantallas cuyo recorrido (los pasos que señalan dónde dar clic) ya se hizo o se omitió. Ausente en lo de antes. */
  recorridos?: string[];
}

/** Ruta + flujo: /pos tiene una ficha por flujo de venta. */
export const claveDeAyuda = (a: { ruta: string; flujo?: string }): string => a.ruta + '?' + (a.flujo ?? '');

/** Por negocio y usuario: el POS conoce el id del usuario (y si no, su nombre); no trae correo. */
export const claveDeEstado = (tenantId: string, usuario: string | number): string => `suresell.ayuda.${tenantId}.${usuario}`;

/** Sin almacenamiento (modo privado, cuota, bloqueo) se comporta como la primera vez y no rompe. */
export function leerEstado(clave: string): EstadoDeBienvenida {
  try {
    const v = JSON.parse(localStorage.getItem(clave) ?? 'null');
    if (v && typeof v.omitido === 'boolean' && Array.isArray(v.vistos)) return { ...v, recorridos: Array.isArray(v.recorridos) ? v.recorridos : [] };
  } catch { /* solo en memoria */ }
  return { omitido: false, vistos: [], recorridos: [] };
}

export function guardarEstado(clave: string, estado: EstadoDeBienvenida): void {
  try { localStorage.setItem(clave, JSON.stringify(estado)); } catch { /* solo en memoria */ }
}

/** ¿Esta ficha es de algo que este negocio ve? Su módulo, y en /pos su flujo de venta. */
export function aplica(a: AyudaDePantalla, tiene: (m: FeatureModule) => boolean, flujo: FlujoDeVenta): boolean {
  return (a.modulo === undefined || tiene(a.modulo)) && (a.flujo === undefined || a.flujo === flujo);
}

/** Las fichas del recorrido que faltan por ver. Con «Omitir», ninguna; un módulo que se activa después sale solo. */
export function pendientes(ayudas: readonly AyudaDePantalla[], tiene: (m: FeatureModule) => boolean, flujo: FlujoDeVenta,
                           estado: EstadoDeBienvenida): AyudaDePantalla[] {
  if (estado.omitido) return [];
  return ayudas.filter(a => a.enLaBienvenida && aplica(a, tiene, flujo) && !estado.vistos.includes(claveDeAyuda(a)));
}

/**
 * La ficha de la pantalla abierta: misma ruta (o subruta) y, en /pos, el flujo de la sede. Ayudas 26/10: gana la ruta más
 * larga (`/bodega/alistar` antes que `/bodega`).
 */
export function ayudaDeLaUrl(ayudas: readonly AyudaDePantalla[], url: string, flujo: FlujoDeVenta): AyudaDePantalla | null {
  const camino = url.split(/[?#]/)[0];
  return ayudas.filter(a => (camino === a.ruta || camino.startsWith(a.ruta + '/')) && (a.flujo === undefined || a.flujo === flujo))
    .sort((x, y) => y.ruta.length - x.ruta.length)[0] ?? null;
}

/** A7: ¿le toca a esta pantalla su recorrido? Solo si tiene pasos y no se hizo ni se omitió antes. */
export function recorridoPendiente(ayuda: AyudaDePantalla | null, estado: EstadoDeBienvenida): AyudaDePantalla | null {
  return ayuda?.pasos?.length && !(estado.recorridos ?? []).includes(claveDeAyuda(ayuda)) ? ayuda : null;
}

/**
 * A7: dónde va la burbuja junto a un elemento, sin salirse de la ventana: a la derecha si es estrecho y está pegado a la
 * izquierda; si no, debajo si cabe, si no encima, y si tampoco, a media altura; en horizontal, recostada contra el borde
 * si hace falta. Sin elemento, al centro. (La misma regla que el panel.)
 */
export function sitioDeLaBurbuja(r: { top: number; left: number; bottom: number; width: number; height: number } | null,
  burbuja: { ancho: number; alto: number }, ventana: { ancho: number; alto: number }, margen = 12): { top: number; left: number } {
  const ancho = Math.min(burbuja.ancho, ventana.ancho - margen * 2);
  if (!r) return { top: Math.max(margen, (ventana.alto - burbuja.alto) / 2), left: Math.max(margen, (ventana.ancho - ancho) / 2) };
  if (r.left < ventana.ancho / 4 && r.width < ventana.ancho / 4 && r.left + r.width + margen + ancho <= ventana.ancho - margen) {
    return { top: Math.max(margen, Math.min(r.top, ventana.alto - burbuja.alto - margen)), left: r.left + r.width + margen };
  }
  const left = Math.max(margen, Math.min(r.left, ventana.ancho - ancho - margen));
  if (r.bottom + margen + burbuja.alto <= ventana.alto - margen) return { top: r.bottom + margen, left };
  if (r.top - margen - burbuja.alto >= margen) return { top: r.top - margen - burbuja.alto, left };
  return { top: Math.max(margen, (ventana.alto - burbuja.alto) / 2), left };
}
